import { MapboxOverlayProps } from "@deck.gl/mapbox";
import { Feature } from "geojson";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { XYLocation } from "@nens/deck-gl";

import { AppDispatch } from "../../../App";
import { addDischargeToSimulation, getPlaceOnMapMode } from "../../../state/dischargeMenu";
import { getSelected } from "../../../state/existingRasterEditsMenu";
import { guiShouldAllowSelectingAssetsOrPoints } from "../../../state/guiStateHelper";
import {
  getPoints as getLeveePoints,
  getStatus as getLeveeStatus,
  setPointsAction as setLeveePointsAction,
} from "../../../state/leveeEdit";
import {
  getActiveSelectionType,
  getActiveTool,
  remindUserToPressPlay,
} from "../../../state/mainGui";
import { getBlockClick, getMap } from "../../../state/map";
import { getFinished, getLine, setFinished, setLine } from "../../../state/mapProfileLine";
import {
  getPoints as getPolygonPoints,
  getStatus as getPolygonStatus,
  setPointsAction as setPolygonPointsAction,
} from "../../../state/polygonEdit";
import * as PumpMenu from "../../../state/pumpMenu";
import { addPumpToSimulation } from "../../../state/pumpMenu";
import { getSelectedAsset, remove } from "../../../state/selectedAsset";
import { getXY, removeXY, setXY } from "../../../state/selectedPoint";
import { displayName } from "../../../types/assets";
import { ToolTip } from "../../DeckGlTypes";

const DeckGlEventsHandler = (): MapboxOverlayProps => {
  const dispatch = useDispatch() as AppDispatch;
  const removeSelectedAsset = () => dispatch(remove());
  const blockClick = useSelector(getBlockClick);
  const activeSelectionType = useSelector(getActiveSelectionType);
  const selectedPointMarker = useSelector(getXY);
  const line = useSelector(getLine);
  const lineFinished = useSelector(getFinished);
  const leveePoints = useSelector(getLeveePoints);
  const leveeEditStatus = useSelector(getLeveeStatus);
  const polygonPoints = useSelector(getPolygonPoints);
  const selectedRasterEdit = useSelector(getSelected);
  const activeTool = useSelector(getActiveTool);
  const polygonStatus = useSelector(getPolygonStatus);
  const dischargePlaceOnMapMode = useSelector(getPlaceOnMapMode);
  const pumpPlaceOnMapMode = useSelector(PumpMenu.getPlaceOnMapMode);
  const [dragging, setDragging] = useState<boolean>(false);
  const guiActive = useSelector(guiShouldAllowSelectingAssetsOrPoints);
  const isDrawingSomething =
    (activeSelectionType === "LINE_SELECTION" && polygonStatus !== "DRAWING_MODE") ||
    leveeEditStatus === "DRAWING_MODE" ||
    polygonStatus === "DRAWING_MODE";
  const map = useSelector(getMap);

  const isSelectable = useSelector(guiShouldAllowSelectingAssetsOrPoints);
  const selectedAsset = useSelector(getSelectedAsset);

  const unSelectItem = (feature: Feature) => {
    if (feature && feature.properties && isSelectable) {
      removeSelectedAsset();
    }
  };

  return {
    onDragStart: () => setDragging(true),
    onDragEnd: () => setDragging(false),
    getCursor: ({ isHovering }: { isHovering: boolean }) => {
      const cursorStyle = isDrawingSomething
        ? "crosshair"
        : isHovering
        ? "pointer"
        : dragging
        ? "grabbing"
        : "grab";

      if (map) map.getCanvas().style.cursor = cursorStyle;
      return cursorStyle;
    },
    getTooltip: ({ object }: ToolTip) => {
      if (!object || !guiActive) return null;
      // Object has complete custom tooltip
      if ("tooltip" in object) return { html: object.tooltip };

      let title = null;
      // Object is confirm or cancel button
      if ("title" in object) title = object.title;
      // Object is an asset
      if ("properties" in object) title = displayName(object);

      // No tooltip
      if (title === null) return null;
      // Default tooltip styling
      return {
        html: `<div>${title}</div>`,
        style: {
          width: "max-content",
          position: "relative",
          left: "10px",
          top: "-1em",
          color: "black",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          borderRadius: "5px",
          padding: "5px",
          lineHeight: "2em",
          fontSize: "1em",
        },
      };
    },
    onClick: (event) => {
      if (blockClick) return;
      const eventCoordinate = event.coordinate as XYLocation;

      if (activeTool === "RASTER_EDIT" && polygonStatus !== "DRAWING_MODE" && selectedRasterEdit) {
        // do not unselect if the click event was not on a div but on another (svg, path etcetera) element
        // without next if statement, clicking on polygon will select polygon, but event will bubble up and trigger here to unselect polygon right away
        // alternative to this if statement we could prevent event bubbling on the polygon. I have not tried that.
        // if (event.originalEvent && event.originalEvent.target) {
        //   removeSelected();
        // }
      } else if (activeTool === "DISCHARGE" && dischargePlaceOnMapMode) {
        if (eventCoordinate) {
          dispatch(addDischargeToSimulation(eventCoordinate));
          dispatch(remindUserToPressPlay());
        }
      } else if (activeTool === "PUMP" && pumpPlaceOnMapMode) {
        if (eventCoordinate) {
          dispatch(addPumpToSimulation(eventCoordinate));
          dispatch(remindUserToPressPlay());
        }
      } else if (activeTool === "LEVEE_TOOL") {
        const currentPoints = leveePoints || [];
        if (leveeEditStatus === "DRAWING_MODE")
          dispatch(setLeveePointsAction([...currentPoints, eventCoordinate]));
      } else if (activeTool === "RASTER_EDIT" && polygonStatus === "DRAWING_MODE") {
        const currentPoints = polygonPoints || [];
        dispatch(setPolygonPointsAction([...currentPoints, eventCoordinate]));
      } else if (activeSelectionType === "LINE_SELECTION" && polygonStatus !== "DRAWING_MODE") {
        const currentLine = !lineFinished && line ? line : [];
        if (lineFinished) dispatch(setFinished(false));
        dispatch(setLine([...currentLine, eventCoordinate]));
      } else {
        if (activeSelectionType !== "SELECTION" || event.picked) return;
        if (selectedAsset) {
          unSelectItem(selectedAsset);
        } else if (eventCoordinate)
          selectedPointMarker ? dispatch(removeXY()) : dispatch(setXY(eventCoordinate));
      }
    },
  };
};

export default DeckGlEventsHandler;
